import { useEffect, useRef, useState } from "react";
import style from "./members-modal.module.css";
import Skeleton from "react-loading-skeleton";
import { User } from "shared/datamodel/schemas";
import useStateValue from "frontend/state/value";
import SendInviteToast from "frontend/ui-components/toast/send-invite-toast";
import { useUsersCache } from "frontend/hooks/caching/use-users-cache";
import SearchEmptyState from "./empty-state/search-empty-state";
import { AccountMember } from "./account-member";
import useInfiniteScroll from "frontend/hooks/use-infinite-scroll";
import { useDebounce } from "frontend/hooks/debounce";
import { CloseIcon } from "frontend/icons/close";
import { useApplyViewerRestricted } from "frontend/hooks/use-apply-viewer-restricted";
import { isBasicPlan } from "shared/util/billing";

export default function AccountMembers({
  title,
  onDismiss,
  onEditSeats,
}: {
  title: string;
  onDismiss: () => void;
  onEditSeats: () => void;
}) {
  const [{ user }] = useStateValue();
  const [members, setMembers] = useState<User[]>([] as User[]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [inviteeName, setInviteeName] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const collaborationModalRef = useRef(null);
  const { isLoading, getUsers, hasMore, searchUsersByName } = useUsersCache();

  const loadMoreUsersByPage = async () => {
    if (debouncedSearchQuery) return;
    const loadedUsers = await getUsers();
    setMembers((previous) => {
      const uniqueUsers = [...previous, ...loadedUsers].reduce((acc, user) => {
        acc[user.id] = user;
        return acc;
      }, {} as Record<string, User>);
      return Object.values(uniqueUsers);
    });
  };

  const loadMoreRef = useInfiniteScroll({
    loadMore: loadMoreUsersByPage,
    hasMore: hasMore && !debouncedSearchQuery,
    isLoading: isLoading || isSearching,
    failedLoadingUsers: false,
  });

  useEffect(() => {
    async function loadAccountUsers() {
      const loadedUsers = await getUsers();
      setMembers(loadedUsers);
    }
    loadAccountUsers();
  }, []);

  useEffect(() => {
    let isCurrentSearch = true;
    const performSearch = async () => {
      if (debouncedSearchQuery.length > 0) {
        setIsSearching(true);
        try {
          const searchResults = await searchUsersByName(debouncedSearchQuery);
          if (isCurrentSearch) {
            setMembers(searchResults);
          }
        } finally {
          if (isCurrentSearch) {
            setIsSearching(false);
          }
        }
      } else {
        const loadedUsers = await getUsers();
        if (isCurrentSearch) {
          setMembers(loadedUsers);
        }
      }
    };
    performSearch();

    return () => {
      isCurrentSearch = false;
    };
  }, [debouncedSearchQuery]);

  const isFreePlan = !user || isBasicPlan(user?.planInfo?.plan_id);
  const applyRestrictedLimitation = useApplyViewerRestricted();
  const isTouchAccount = user?.planInfo?.source === "touch";
  const canManageSeats = !isFreePlan && !isTouchAccount && user.isAdmin && user.isSubscribedToCanvas;
  const isOwner = user?.isAdmin;

  useEffect(() => {
    let timeOut: NodeJS.Timeout;
    if (inviteeName) {
      timeOut = setTimeout(() => {
        setInviteeName(null);
      }, 6000);
    }
    return () => timeOut && clearTimeout(timeOut);
  }, [inviteeName]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const renderMembers = () => {
    if (members.length === 0 && !isLoading && !isSearching) {
      return <SearchEmptyState searchTerm={searchQuery} subtitle="in project members" />;
    }
    const sortedMembersBySubscription = !applyRestrictedLimitation
      ? members
      : [...members].sort((a, b) => {
          if (a.isSubscribedToCanvas && !b.isSubscribedToCanvas) return 1;
          if (!a.isSubscribedToCanvas && b.isSubscribedToCanvas) return -1;
          return 0;
        });

    return sortedMembersBySubscription?.map((teammate, index) => (
      <AccountMember key={teammate.id} teammate={teammate} index={index} isOwner={isOwner} />
    ));
  };

  return (
    <div className={style.container} ref={collaborationModalRef}>
      <div className={style.headerContainer}>
        <div className={style.header}>
          <div className={style.closeIcon}>
            <CloseIcon onClick={onDismiss} />
          </div>
          <p className={style.title}>{title}</p>
        </div>
        <div className={style.search}>
          <div className={style.subtitleContainer}>
            <span className={style.subtitle}>{`Search an account member`}</span>
            {canManageSeats && (
              <span className={style.manageSeats} onClick={onEditSeats}>
                Manage seats
              </span>
            )}
          </div>
          <div className={style.searchContainer}>
            <input
              type="text"
              placeholder="Search members..."
              value={searchQuery}
              onChange={handleSearch}
              className={style.searchInput}
              autoFocus={true}
            />
          </div>
        </div>
      </div>
      <div className={style.divider}></div>
      <div className={style.collaborators}>
        {renderMembers()}
        <div ref={loadMoreRef}>
          {isLoading ? (
            <Skeleton count={4} className={style.userLineSkeleton} containerClassName={style.collaborators} />
          ) : null}
        </div>
      </div>
      {inviteeName && <SendInviteToast inviteeName={inviteeName} />}
    </div>
  );
}
